import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { fetchStudentsByFamily } from '../../services/studentService';
import { deriveGuardians, formatPhone } from '../../services/familyMembers';
import { PageWrap, PageHead, Card, SectionHead, studentDisplayName } from './customerUi';

/* Family — a READ-ONLY view of who is on the account: GUARDIANS and STUDENTS.
   Editing family details isn't a customer-writable flow, so this surfaces who
   is linked and points changes to the studio.

   IT SHOWS GUARDIANS AS WELL AS STUDENTS AS OF 2026-08-10, and the reason is
   that the page's own promise was false without them. The subtitle has always
   read "Everyone linked to your account" while the body listed children only —
   so the one person definitely linked to the account, the parent reading it,
   was the one name missing. Anthony's portal review that morning asked to
   "expand the Family page to allow adding additional family members… or defer
   the feature until a plan is defined": this is the honest half of that, done
   now, with MANAGEMENT deliberately deferred to its own card.

   Guardians are DERIVED from the students' parent1/parent2 fields, not read
   directly, because the rules leave no other route — see the header of
   services/familyMembers.js. That keeps this page on the single familyId-scoped
   read a customer is granted, with no rules change. */

const initial = (s) => (studentDisplayName(s)[0] || 'S').toUpperCase();

const rowStyle = (isLast) => ({
	display: 'flex',
	alignItems: 'center',
	gap: 12,
	padding: '13px 15px',
	borderBottom: isLast ? 'none' : '1px solid var(--cyv-line, #f0eae1)',
});

const avatar = (bg, color) => ({
	width: 40,
	height: 40,
	borderRadius: '50%',
	background: bg,
	color,
	fontWeight: 'var(--fw-bold, 700)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '0 0 auto',
});

const nameStyle = {
	display: 'block',
	fontSize: '0.95rem',
	fontWeight: 'var(--fw-bold, 700)',
	color: 'var(--text-heading, #384971)',
};

const subStyle = {
	display: 'block',
	fontSize: '0.8rem',
	color: 'var(--text-muted, #7b8499)',
	marginTop: 2,
	overflowWrap: 'anywhere',
};

const Family = () => {
	const { currentUser, userProfile } = useAuth();
	const familyId = userProfile?.familyId || null;

	const [students, setStudents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return undefined;
		}
		let cancelled = false;
		(async () => {
			try {
				const kids = await fetchStudentsByFamily(familyId);
				if (!cancelled) setStudents(kids);
			} catch (err) {
				console.error('Error loading family:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [familyId]);

	const subFor = (s) =>
		[s.instrument, s.pronouns].filter(Boolean).join(' · ') || 'Student';

	const guardians = useMemo(
		() => deriveGuardians(students, currentUser?.email),
		[students, currentUser]
	);

	return (
		<PageWrap>
			<PageHead
				title='Family'
				subtitle='Everyone linked to your account.'
			/>

			{loading && (
				<p style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
					Loading…
				</p>
			)}

			{!loading && loadError && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						We couldn’t load your family just now. Please refresh, or{' '}
						<a href='/contact'>contact us</a> if it keeps happening.
					</p>
				</Card>
			)}

			{!loading && !loadError && !familyId && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						Your account isn’t linked to a family yet. If you think this is a
						mistake, <a href='/contact'>contact us</a> and we’ll get you set up.
					</p>
				</Card>
			)}

			{!loading && !loadError && familyId && students.length === 0 && (
				<Card style={{ padding: '15px' }}>
					<p style={{ margin: 0, color: 'var(--text-body)' }}>
						No students are linked to your account yet. If that doesn’t look
						right, <a href='/contact'>contact us</a>.
					</p>
				</Card>
			)}

			{!loading && !loadError && students.length > 0 && (
				<>
					{/* Guardians first: this is the "who is on the account" question,
					    and the reader expects to find themselves. Suppressed entirely
					    when none resolve, rather than rendering an empty shell — a
					    family whose student docs carry no parent details is a data
					    gap for the studio to fix, not something to explain here. */}
					{guardians.length > 0 && (
						<Card style={{ marginBottom: 14 }}>
							<SectionHead icon='fa-user-group' label='Grown-ups' />
							{guardians.map((g, i) => (
								<div key={g.key} style={rowStyle(i === guardians.length - 1)}>
									<span
										style={avatar(
											'color-mix(in srgb, var(--cyv-navy, #384971) 14%, var(--surface-card, #fff))',
											'var(--text-heading, #384971)'
										)}>
										{(g.name[0] || 'G').toUpperCase()}
									</span>
									<span style={{ flex: 1, minWidth: 0 }}>
										<span style={nameStyle}>
											{g.name}
											{g.isViewer && (
												<span
													style={{
														marginLeft: 7,
														fontSize: '0.7rem',
														fontWeight: 'var(--fw-bold, 700)',
														letterSpacing: '0.06em',
														textTransform: 'uppercase',
														color: 'var(--cyv-teal-600, #2f7d77)',
													}}>
													You
												</span>
											)}
										</span>
										<span style={subStyle}>
											{[g.email, formatPhone(g.phone)].filter(Boolean).join(' · ') ||
												'No contact details on file'}
										</span>
									</span>
								</div>
							))}
						</Card>
					)}

					<Card style={{ marginBottom: 14 }}>
						<SectionHead icon='fa-music' label='Students' />
						{students.map((s, i) => (
							<div key={s.id} style={rowStyle(i === students.length - 1)}>
								<span
									style={avatar(
										'color-mix(in srgb, var(--cyv-teal-600, #54ada7) 18%, var(--surface-card, #fff))',
										'var(--cyv-teal-600, #2f7d77)'
									)}>
									{initial(s)}
								</span>
								<span style={{ flex: 1, minWidth: 0 }}>
									<span style={nameStyle}>{studentDisplayName(s)}</span>
									<span style={subStyle}>{subFor(s)}</span>
								</span>
							</div>
						))}
					</Card>

					<div
						style={{
							fontSize: '0.82rem',
							color: 'var(--text-muted, #7b8499)',
							lineHeight: 1.55,
							padding: '0 2px',
						}}>
						Signed in as {currentUser?.email}. To add a grown-up or a student, or
						to change anyone’s details, <a href='/contact'>get in touch</a> and
						we’ll take care of it.
					</div>
				</>
			)}
		</PageWrap>
	);
};

export default Family;
